import React, { ReactNode, useCallback, useRef, useState } from "react";
import DialogContext, { BaseDialogProps, DialogComponent, DialogOptions } from "./dialog-context";

interface DialogState<T extends BaseDialogProps> {
  component: DialogComponent<T>;
  props: Omit<T, keyof BaseDialogProps>;
  persistent: boolean;
  open: boolean;
}

interface DialogProviderProps {
  children: ReactNode;
}

const DialogProvider: React.FC<DialogProviderProps> = ({ children }) => {
  const [dialogs, setDialogs] = useState<Record<string, DialogState<BaseDialogProps>>>({});
  const dialogResolutions = useRef<Record<string, (value: boolean) => void>>({});
  const dialogCounter = useRef(0);

  const closeDialog = useCallback((dialogKey: string, completed: boolean) => {
    setDialogs((prevDialogs) => {
      const dialog = prevDialogs[dialogKey];
      if (!dialog) return prevDialogs;

      return dialog.persistent
        ? {
            ...prevDialogs,
            [dialogKey]: { ...dialog, open: false }
          }
        : Object.fromEntries(Object.entries(prevDialogs).filter(([key]) => key !== dialogKey));
    });

    if (dialogResolutions.current[dialogKey]) {
      dialogResolutions.current[dialogKey](completed);
      delete dialogResolutions.current[dialogKey];
    }
  }, []);

  const openDialog = useCallback(
    <T extends BaseDialogProps>(
      DialogComponent: DialogComponent<T>,
      props: Omit<T, keyof BaseDialogProps> = {} as Omit<T, keyof BaseDialogProps>,
      options?: DialogOptions
    ): Promise<boolean> => {
      const { persistent = false, dialogKey: providedDialogKey } = options || {};
      const dialogKey = providedDialogKey || `dialog_${dialogCounter.current++}`;

      return new Promise<boolean>((resolve) => {
        setDialogs((prevDialogs) => ({
          ...prevDialogs,
          [dialogKey]: {
            component: DialogComponent as DialogComponent<BaseDialogProps>,
            props,
            persistent,
            open: true
          }
        }));

        dialogResolutions.current[dialogKey] = resolve;
      });
    },
    []
  );

  return (
    <DialogContext.Provider value={{ openDialog }}>
      {children}
      {Object.entries(dialogs).map(([dialogKey, dialog]) => {
        const DialogComponent = dialog.component;
        return (
          <DialogComponent
            key={dialogKey}
            {...dialog.props}
            open={dialog.open}
            onClose={(completed: boolean) => closeDialog(dialogKey, completed)}
          />
        );
      })}
    </DialogContext.Provider>
  );
};

export default DialogProvider;
